import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  Snackbar,
  FormControlLabel as MuiFormControlLabel,
  RadioGroup,
  Radio,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Cake as CakeIcon,
  School as SchoolIcon,
  AccountCircle as AccountIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { SingleImageDisplay } from '../../components/ImageDisplay';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

interface Parent {
  _id: string;
  fullName: string;
  email: string;
  phone: string;
  birthDate: string;
  relationship: string;
  photo: string;
  children: Array<{
    _id: string;
    fullName: string;
  }>;
  kindergarten?: {
    _id: string;
    name: string;
    location?: string;
  };
  createdAt: string;
  updatedAt: string;
  hasAccount?: boolean;
}

interface Child {
  _id: string;
  fullName: string;
}

interface Kindergarten {
  _id: string;
  name: string;
  location?: string;
}

interface ParentFormData {
  fullName: string;
  email: string;
  phone: string;
  birthDate: string;
  relationship: string;
  photo: File | null;
  childrenIds: string[];
  kindergartenId?: string;
}

interface AccountCreationResult {
  email: string;
  password: string;
  parentName: string;
}

interface PasswordChangeResult {
  email: string;
  newPassword: string;
  parentName: string;
}

const Parents: React.FC = () => {
  const { hasPermission } = useAdminAuth();
  const [parents, setParents] = useState<Parent[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [kindergartens, setKindergartens] = useState<Kindergarten[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editingParent, setEditingParent] = useState<Parent | null>(null);
  const [viewingParent, setViewingParent] = useState<Parent | null>(null);
  const [formData, setFormData] = useState<ParentFormData>({
    fullName: '',
    email: '',
    phone: '',
    birthDate: '',
    relationship: 'мама',
    photo: null,
    childrenIds: [],
  });
  const [accountResult, setAccountResult] = useState<AccountCreationResult | null>(null);
  const [showAccountResult, setShowAccountResult] = useState(false);
  
  // Стани для зміни пароля
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [selectedParentForPassword, setSelectedParentForPassword] = useState<Parent | null>(null);
  const [passwordMethod, setPasswordMethod] = useState<'generate' | 'manual'>('generate');
  const [manualPassword, setManualPassword] = useState('');
  const [passwordResult, setPasswordResult] = useState<PasswordChangeResult | null>(null);
  const [showPasswordResult, setShowPasswordResult] = useState(false);

  // Налаштування axios
  const api = axios.create({
    baseURL: 'https://api.smartik.com.ua/api'
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [parentsRes, childrenRes, kindergartensRes] = await Promise.all([
        api.get(`/parents?page=${page}&limit=${itemsPerPage}`),
        api.get('/children/lookup?limit=1000'),
        api.get('/kindergartens?limit=1000'),
      ]);
      
      setParents(parentsRes.data.data || []);
      setChildren(childrenRes.data.data || []);
      setKindergartens(kindergartensRes.data.data || []);
      
      // Встановлюємо дані пагінації
      if (parentsRes.data.pagination) {
        setTotalPages(parentsRes.data.pagination.totalPages);
        setTotalItems(parentsRes.data.pagination.totalItems);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка завантаження даних');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (parent?: Parent) => {
    if (parent) {
      setEditingParent(parent);
      setFormData({
        fullName: parent.fullName,
        email: parent.email,
        phone: parent.phone,
        birthDate: parent.birthDate.split('T')[0],
        relationship: parent.relationship || 'мама',
        photo: null,
        childrenIds: parent.children.map(child => child._id),
        kindergartenId: parent.kindergarten?._id,
      });
    } else {
      setEditingParent(null);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        birthDate: '',
        relationship: 'мама',
        photo: null,
        childrenIds: [],
        kindergartenId: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingParent(null);
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      birthDate: '',
      relationship: 'мама',
      photo: null,
      childrenIds: [],
      kindergartenId: '',
    });
  };

  const handleOpenViewDialog = (parent: Parent) => {
    setViewingParent(parent);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewingParent(null);
  };

  const handleSubmit = async () => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('fullName', formData.fullName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('birthDate', formData.birthDate);
      formDataToSend.append('relationship', formData.relationship);
      formDataToSend.append('children', JSON.stringify(formData.childrenIds));
      
      if (formData.kindergartenId) {
        formDataToSend.append('kindergarten', formData.kindergartenId);
      }

      if (formData.photo) {
        formDataToSend.append('photo', formData.photo);
      }

      if (editingParent) {
        await api.put(`/parents/${editingParent._id}`, formDataToSend);
        handleCloseDialog();
        fetchData();
      } else {
        // Створюємо батька та обліковий запис
        const response = await api.post('/parents/create-with-account', formDataToSend);
        
        if (response.data.success && response.data.accountData) {
          setAccountResult({
            email: response.data.accountData.email,
            password: response.data.accountData.password,
            parentName: formData.fullName,
          });
          setShowAccountResult(true);
        }
        
        handleCloseDialog();
        setPage(1); // Повертаємось на першу сторінку після додавання
        fetchData();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка збереження');
    }
  };

  const handleCreateAccount = async (parentId: string) => {
    try {
      const response = await api.post(`/parents/${parentId}/create-account`);
      
      if (response.data.success && response.data.accountData) {
        setAccountResult({
          email: response.data.accountData.email,
          password: response.data.accountData.password,
          parentName: response.data.accountData.parentName,
        });
        setShowAccountResult(true);
        await fetchData(); // Оновлюємо дані щоб показати що акаунт створено
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка створення облікового запису');
    }
  };

  const handleOpenPasswordDialog = (parent: Parent) => {
    setSelectedParentForPassword(parent);
    setPasswordMethod('generate');
    setManualPassword('');
    setOpenPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setSelectedParentForPassword(null);
    setPasswordMethod('generate');
    setManualPassword('');
  };

  const handleChangePassword = async () => {
    if (!selectedParentForPassword) return;

    try {
      // Спочатку знаходимо користувача за email батька
      const usersResponse = await api.get(`/users?role=parent`);
      const user = usersResponse.data.data.find((u: any) => u.email === selectedParentForPassword.email);
      
      if (!user) {
        setError('Користувача не знайдено');
        return;
      }

      const requestData = passwordMethod === 'generate' 
        ? { generateNew: true }
        : { password: manualPassword };

      const response = await api.patch(`/users/${user._id}/password`, requestData);
      
      if (response.data.success) {
        setPasswordResult({
          email: response.data.data.user.email,
          newPassword: response.data.data.newPassword,
          parentName: response.data.data.user.parentName,
        });
        setShowPasswordResult(true);
        handleClosePasswordDialog();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка зміни пароля');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Ви впевнені, що хочете видалити цього батька?')) {
      try {
        await api.delete(`/parents/${id}`);
        // Якщо на поточній сторінці залишився тільки один елемент і це не перша сторінка, повертаємось назад
        if (parents.length === 1 && page > 1) {
          setPage(page - 1);
        } else {
          fetchData();
        }
      } catch (error: any) {
        setError(error.response?.data?.message || 'Помилка видалення');
      }
    }
  };

  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Батьки
        </Typography>
        {hasPermission('parents', 'create') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            onClick={() => handleOpenDialog()}
          >
            Додати батька
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Показано {parents.length} з {totalItems} батьків
            </Typography>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={(e, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Фото</TableCell>
                  <TableCell>ПІБ</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Телефон</TableCell>
                  <TableCell>Вік</TableCell>
                  <TableCell>Садок</TableCell>
                  <TableCell>Діти</TableCell>
                  <TableCell>Обліковий запис</TableCell>
                  <TableCell>Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parents.map((parent) => (
                  <TableRow key={parent._id}>
                    <TableCell>
                      <SingleImageDisplay
                        image={parent.photo}
                        size={40}
                        title="Фото батька"
                        placeholderIcon={<PersonIcon />}
                        placeholderText="Немає фото"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {parent.fullName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {parent.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {parent.phone}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CakeIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {calculateAge(parent.birthDate)} років
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {parent.kindergarten ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SchoolIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {parent.kindergarten.name}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Не вказано
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {parent.children.map((child, index) => (
                          <Chip
                            key={index}
                            label={child.fullName}
                            size="small"
                            variant="outlined"
                            icon={<SchoolIcon />}
                          />
                        ))}
                        {parent.children.length === 0 && (
                          <Chip label="Немає дітей" size="small" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {parent.hasAccount ? (
                        <Chip 
                          label="Створено" 
                          color="success" 
                          size="small" 
                          icon={<AccountIcon />}
                        />
                      ) : (
                        hasPermission('parents', 'create') && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AccountIcon />}
                            onClick={() => handleCreateAccount(parent._id)}
                          >
                            Створити акаунт
                          </Button>
                        )
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Переглянути">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenViewDialog(parent)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {hasPermission('parents', 'update') && (
                          <Tooltip title="Редагувати">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenDialog(parent)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {parent.hasAccount && hasPermission('parents', 'update') && (
                          <Tooltip title="Змінити пароль">
                            <IconButton 
                              size="small" 
                              color="warning"
                              onClick={() => handleOpenPasswordDialog(parent)}
                            >
                              <LockIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('parents', 'delete') && (
                          <Tooltip title="Видалити">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDelete(parent._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={(e, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
              size="large"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Діалог додавання/редагування */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingParent ? 'Редагувати батька' : 'Додати батька'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Повне ім'я"
              value={formData.fullName}
              onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Телефон"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Дата народження"
              type="date"
              value={formData.birthDate}
              onChange={(e) => setFormData(prev => ({ ...prev, birthDate: e.target.value }))}
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Фото батька
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setFormData(prev => ({ ...prev, photo: file }));
              }}
            />
            {formData.photo && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Завантажене фото:
                </Typography>
                <Avatar
                  src={URL.createObjectURL(formData.photo)}
                  sx={{ width: 80, height: 80 }}
                />
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Дитячий садок
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Виберіть дитячий садок</InputLabel>
              <Select
                value={formData.kindergartenId || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  kindergartenId: e.target.value 
                }))}
                label="Виберіть дитячий садок"
              >
                <MenuItem value="">
                  <em>Не вказано</em>
                </MenuItem>
                {kindergartens.map((kindergarten) => (
                  <MenuItem key={kindergarten._id} value={kindergarten._id}>
                    {kindergarten.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Діти
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Виберіть дітей</InputLabel>
              <Select
                multiple
                value={formData.childrenIds}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  childrenIds: typeof e.target.value === 'string' ? [] : e.target.value 
                }))}
                label="Виберіть дітей"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((childId) => {
                      const child = children.find(c => c._id === childId);
                      return child ? (
                        <Chip key={childId} label={child.fullName} size="small" />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {children.map((child) => (
                  <MenuItem key={child._id} value={child._id}>
                    {child.fullName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingParent ? 'Зберегти' : 'Додати'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог з результатом створення облікового запису */}
      <Dialog 
        open={showAccountResult} 
        onClose={() => setShowAccountResult(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Обліковий запис створено
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Для батька <strong>{accountResult?.parentName}</strong> створено обліковий запис:
            </Typography>
            
            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Email:</strong> {accountResult?.email}
              </Typography>
              <Typography variant="body2">
                <strong>Пароль:</strong> {accountResult?.password}
              </Typography>
            </Box>
            
            <Alert severity="warning">
              Збережіть ці дані! Пароль не можна буде подивитися пізніше.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAccountResult(false)} variant="contained">
            Зрозуміло
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог зміни пароля */}
      <Dialog 
        open={openPasswordDialog} 
        onClose={handleClosePasswordDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Змінити пароль
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Батько: <strong>{selectedParentForPassword?.fullName}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Email: {selectedParentForPassword?.email}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Виберіть спосіб зміни пароля:
              </Typography>
              <RadioGroup
                value={passwordMethod}
                onChange={(e) => setPasswordMethod(e.target.value as 'generate' | 'manual')}
              >
                <MuiFormControlLabel
                  value="generate"
                  control={<Radio />}
                  label="Згенерувати новий пароль автоматично"
                />
                <MuiFormControlLabel
                  value="manual"
                  control={<Radio />}
                  label="Ввести пароль вручну"
                />
              </RadioGroup>
            </FormControl>

            {passwordMethod === 'manual' && (
              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  label="Новий пароль"
                  type="password"
                  value={manualPassword}
                  onChange={(e) => setManualPassword(e.target.value)}
                  helperText="Мінімум 6 символів"
                  error={manualPassword.length > 0 && manualPassword.length < 6}
                />
              </Box>
            )}

            {passwordMethod === 'generate' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Пароль буде згенерований автоматично з 8 символів (літери та цифри)
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>Скасувати</Button>
          <Button 
            onClick={handleChangePassword} 
            variant="contained"
            disabled={passwordMethod === 'manual' && (manualPassword.length < 6)}
          >
            Змінити пароль
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог перегляду батька */}
      <Dialog 
        open={openViewDialog} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Інформація про батька
        </DialogTitle>
        <DialogContent>
          {viewingParent && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <SingleImageDisplay
                  image={viewingParent.photo}
                  size={80}
                  title="Фото батька"
                  placeholderIcon={<PersonIcon />}
                  placeholderText="Немає фото"
                />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {viewingParent.fullName}
                  </Typography>
                  <Chip 
                    label={viewingParent.relationship} 
                    color="primary" 
                    size="small"
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    {viewingParent.email}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Телефон
                  </Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    {viewingParent.phone}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Дата народження
                  </Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CakeIcon fontSize="small" color="action" />
                    {new Date(viewingParent.birthDate).toLocaleDateString('uk-UA')} 
                    ({calculateAge(viewingParent.birthDate)} років)
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Дитячий садок
                  </Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SchoolIcon fontSize="small" color="action" />
                    {viewingParent.kindergarten ? viewingParent.kindergarten.name : 'Не вказано'}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Діти
                </Typography>
                {viewingParent.children && viewingParent.children.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {viewingParent.children.map((child, index) => (
                      <Chip
                        key={index}
                        label={child.fullName}
                        size="medium"
                        variant="outlined"
                        icon={<SchoolIcon />}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Немає дітей
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Обліковий запис
                </Typography>
                {viewingParent.hasAccount ? (
                  <Chip 
                    label="Створено" 
                    color="success" 
                    icon={<AccountIcon />}
                  />
                ) : (
                  <Chip 
                    label="Не створено" 
                    color="default" 
                    icon={<AccountIcon />}
                  />
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Додаткова інформація
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Дата створення
                    </Typography>
                    <Typography variant="body2">
                      {new Date(viewingParent.createdAt).toLocaleDateString('uk-UA')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Останнє оновлення
                    </Typography>
                    <Typography variant="body2">
                      {new Date(viewingParent.updatedAt).toLocaleDateString('uk-UA')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Закрити</Button>
        </DialogActions>
      </Dialog>

      {/* Діалог з результатом зміни пароля */}
      <Dialog 
        open={showPasswordResult} 
        onClose={() => setShowPasswordResult(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Пароль змінено
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Для батька <strong>{passwordResult?.parentName}</strong> змінено пароль:
            </Typography>
            
            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Email:</strong> {passwordResult?.email}
              </Typography>
              <Typography variant="body2">
                <strong>Новий пароль:</strong> {passwordResult?.newPassword}
              </Typography>
            </Box>
            
            <Alert severity="warning">
              Збережіть ці дані! Пароль не можна буде подивитися пізніше.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordResult(false)} variant="contained">
            Зрозуміло
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Parents; 